import { debounce } from 'throttle-debounce';
import { SETTINGS } from '../../utils/constants';

const SEARCH_SUBMIT_TOGGLE_SELECTOR = '[data-search-submit-toggle]';

const bindEmptyQueryRedirect = (form) => {
  if (!form || form.dataset.emptyQueryRedirectInit === '1') return;
  form.dataset.emptyQueryRedirectInit = '1';

  form.addEventListener('submit', (e) => {
    const input = form.querySelector('input[name="q"]');
    if (input && input.value.trim().length === 0) {
      e.preventDefault();
      window.location.assign(`${window.Shopify.routes.root}collections/all`);
    }
  });
};

/**
 * Toggle submit vs clear on the trailing control: search icon when empty, close when typed.
 */
const bindSearchSubmitToggle = (input, button, { onClear } = {}) => {
  if (!input || !button) return;

  const searchIconWrap = button.querySelector('[data-search-trigger-icon]');
  const clearIconWrap = button.querySelector('[data-search-clear-icon]');
  const labelSearch = button.getAttribute('data-aria-search') || 'Search';
  const labelClear = button.getAttribute('data-aria-clear') || 'Clear';

  const sync = () => {
    const has = input.value.trim().length > 0;
    button.type = has ? 'button' : 'submit';
    button.setAttribute('aria-label', has ? labelClear : labelSearch);
    searchIconWrap?.classList.toggle('hidden', has);
    clearIconWrap?.classList.toggle('hidden', !has);
  };

  button.addEventListener('click', (e) => {
    if (button.type !== 'button') return;
    e.preventDefault();
    e.stopPropagation();
    input.value = '';
    input.focus();
    sync();
    if (typeof onClear === 'function') onClear();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  input.addEventListener('input', sync);
  input.addEventListener('change', sync);
  sync();
};

const initSearchBannerForms = () => {
  document.querySelectorAll('[data-search-banner-form]').forEach((form) => {
    bindEmptyQueryRedirect(form);
    if (form.dataset.searchBannerToggleInit === '1') return;
    form.dataset.searchBannerToggleInit = '1';
    const input = form.querySelector('input[type="search"]');
    const btn = form.querySelector(SEARCH_SUBMIT_TOGGLE_SELECTOR);
    bindSearchSubmitToggle(input, btn, { onClear: () => {} });
  });
};

const initHeaderInlineSearchPlaceholder = () => {
  const input = document.querySelector('[data-header-search-input]');
  if (!input) return;

  /* #main-search: Liquid placeholder + Algolia detached ::before match — no typing animation (avoids flash at Algolia handoff). */
  if (input.closest('[data-predictive-search]')) return;

  const fullText = input.dataset.placeholderText || '';
  if (!fullText) return;

  const typingDelayMs = 45;
  const pauseAfterTypedMs = 1200;

  let typeInterval;
  let pauseTimeout;
  let index = 0;

  const clearTimers = () => {
    if (typeInterval) window.clearInterval(typeInterval);
    if (pauseTimeout) window.clearTimeout(pauseTimeout);
  };

  const runTyping = () => {
    clearTimers();
    input.setAttribute('placeholder', '');
    index = 0;

    typeInterval = window.setInterval(() => {
      index += 1;
      input.setAttribute('placeholder', fullText.slice(0, index));
      if (index >= fullText.length) {
        window.clearInterval(typeInterval);
        pauseTimeout = window.setTimeout(() => {
          if (document.activeElement !== input && !input.value) {
            runTyping();
          }
        }, pauseAfterTypedMs);
      }
    }, typingDelayMs);
  };

  input.addEventListener('focus', () => {
    clearTimers();
    input.setAttribute('placeholder', fullText);
  });

  input.addEventListener('blur', () => {
    if (!input.value) {
      runTyping();
    }
  });

  runTyping();
};

const initSearch = () => {
  initHeaderInlineSearchPlaceholder();
  initSearchBannerForms();
  document.querySelectorAll('[data-predictive-search-form]').forEach(bindEmptyQueryRedirect);

  const predictiveSearchEl = document.querySelector('[data-predictive-search]');
  const settings = JSON.parse(predictiveSearchEl?.dataset.settings || '{}');
  if (!predictiveSearchEl) return;

  const searchInputEl = predictiveSearchEl.querySelector('input[type="search"]');
  const resultsEl = predictiveSearchEl.querySelector('[data-predictive-search-results]');
  const overlayEl = predictiveSearchEl.querySelector('[data-predictive-search-overlay]');
  if (!searchInputEl || !resultsEl) return;

  const openResults = () => {
    resultsEl.classList.remove('hidden');
    overlayEl?.classList.remove('hidden');
  };

  const closeResults = ({ clearInput = true } = {}) => {
    if (clearInput) {
      searchInputEl.value = '';
    }
    resultsEl.innerHTML = '';
    resultsEl.classList.add('hidden');
    overlayEl?.classList.add('hidden');
  };

  const fetchResults = async (query) => {
    const url = `${window.location.origin}/search/suggest?q=${encodeURIComponent(query)}&resources[type]=query,${settings?.type}&resources[options][fields]=${settings?.fields}&resources[limit]=${settings?.limit}&resources[options][unavailable_products]=${settings?.oos}&section_id=predictive-search`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        closeResults();
        throw new Error(`Fetch failed: ${response.status}`);
      }

      const htmlText = await response.text();
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      const section = html.querySelector('#shopify-section-predictive-search');

      if (section) {
        resultsEl.innerHTML = section.innerHTML;
        openResults();
      } else {
        closeResults();
      }
    } catch (error) {
      closeResults();
      console.error('Search error:', error);
    }
  };

  const handleInput = () => {
    const value = searchInputEl.value.trim();
    if (value.length < settings.trigger) {
      closeResults({ clearInput: false });
      return;
    }
    fetchResults(value);
  };

  const debouncedHandleInput = debounce(SETTINGS.SEARCH_DEBOUNCE_DELAY, handleInput);
  searchInputEl.addEventListener('input', debouncedHandleInput);
  searchInputEl.addEventListener('search', () => {
    if (!searchInputEl.value.trim()) {
      closeResults();
    }
  });

  const predictiveSubmitBtn = predictiveSearchEl.querySelector(SEARCH_SUBMIT_TOGGLE_SELECTOR);
  bindSearchSubmitToggle(searchInputEl, predictiveSubmitBtn, {
    onClear: () => closeResults({ clearInput: false }),
  });

  overlayEl?.addEventListener('click', () => {
    closeResults({ clearInput: false });
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (predictiveSearchEl.contains(target)) return;
    if (typeof target.closest === 'function' && target.closest('.aa-Panel')) return;
    closeResults({ clearInput: false });
  });

  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape' && !resultsEl.classList.contains('hidden')) {
      closeResults({ clearInput: false });
      searchInputEl.blur();
    }
  });
};

export default initSearch;
